import { Injectable, signal } from '@angular/core';

import { UserRole } from '@shared-enums';

import { ALL_MENU_ITEMS } from '../constants/menu-items.constant';
import { MenuItem } from '../models';
import {
  MenuItemDefinition,
  VALID_ROLES,
} from '../models/menu-item-definition.model';

/**
 * Menu service with role-based access control
 */
@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private menuItems = signal<MenuItem[]>([]);
  private menuCache = new Map<string, MenuItem[]>();

  private readonly allMenuItems: MenuItemDefinition[] = ALL_MENU_ITEMS;

  getMenuItems() {
    return this.menuItems.asReadonly();
  }

  setMenuItemsByRole(roleId: string): void {
    if (!VALID_ROLES.has(roleId)) {
      console.warn(`Invalid role: ${roleId}. Setting empty menu.`);
      this.menuItems.set([]);
      return;
    }

    const items = this.getMenuItemsForRole(roleId);
    this.menuItems.set(items);
  }

  clearCache(): void {
    this.menuCache.clear();
  }

  getAllAvailableMenuItems(): MenuItemDefinition[] {
    return [...this.allMenuItems];
  }

  hasAccessToMenuItem(roleId: string, menuItemId: string): boolean {
    const item = this.allMenuItems.find((item) => item.id === menuItemId);
    return item ? item.roles.includes(roleId as UserRole) : false;
  }

  /**
   * Looks up the roles allowed for a given route path using ALL_MENU_ITEMS
   * as the single source of truth, so route guards don't duplicate the matrix.
   * Returns undefined when the route isn't represented in the menu.
   */
  getRolesForRoute(routePath: string): UserRole[] | undefined {
    return this.findMenuItemByRoute(this.allMenuItems, routePath)?.roles;
  }

  private findMenuItemByRoute(
    items: MenuItemDefinition[],
    routePath: string,
  ): MenuItemDefinition | undefined {
    for (const item of items) {
      if (item.route === routePath) {
        return item;
      }

      const foundInChildren =
        item.children && this.findMenuItemByRoute(item.children, routePath);
      if (foundInChildren) {
        return foundInChildren;
      }
    }

    return undefined;
  }

  private getMenuItemsForRole(roleId: string): MenuItem[] {
    const cached = this.menuCache.get(roleId);
    if (cached) {
      return cached;
    }

    const items = this.allMenuItems
      .filter(
        (item) =>
          item.visibleInMenu !== false &&
          item.roles.includes(roleId as UserRole),
      )
      .map((item) => this.transformToMenuItem(item, roleId));

    this.menuCache.set(roleId, items);
    return items;
  }

  private transformToMenuItem(
    item: MenuItemDefinition,
    roleId: string,
  ): MenuItem {
    const menuItem: MenuItem = {
      id: item.id,
      name: item.name,
      icon: item.icon,
      route: item.route,
      action: item.action,
    };

    if (item.children) {
      const accessibleChildren = item.children
        .filter(
          (child) =>
            child.visibleInMenu !== false &&
            child.roles.includes(roleId as UserRole),
        )
        .map((child) => this.transformToMenuItem(child, roleId));

      if (accessibleChildren.length > 0) {
        menuItem.children = accessibleChildren;
      }
    }

    return menuItem;
  }
}
